import { FC, ReactNode, useMemo, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import useMeasure from 'react-use-measure'
import {
  Text,
  GreyGrey10,
  GreyGrey30,
  GreyGrey40,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  Primary13Regular,
  Primary13SemiBold,
  Skeleton,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { format } from '@sequencehq/utils/dates'
import type { Currency } from '@sequencehq/api'
import type { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import type { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import { useInvoiceDueDateFormatter } from 'Invoices/hooks/useInvoiceDueDateFormatter'
import { getHumanReadableInvoiceNumber } from 'Invoices/utils/getHumanReadableInvoiceNumber'
import { Card } from './NodeTimeline/Card'
import { CustomerLogoCircle } from './CustomerLogoCircle'
import { crossFadeMotionProps } from './previewCardMotion'
import {
  INVOICE_PREVIEW_CARD_DUE_DATE_TESTID,
  INVOICE_PREVIEW_CARD_SKELETON_TESTID,
  INVOICE_PREVIEW_CARD_TITLE_TESTID
} from './InvoicePreviewCard.constants'

type Invoice = Pick<
  Dashboardv99990101Api.Invoices.Invoice,
  | 'invoiceNumber'
  | 'status'
  | 'currency'
  | 'grossTotal'
  | 'netTotal'
  | 'dueDate'
>

type Customer = Pick<
  Dashboardv99990101CommonModels.Customers.Customer,
  'legalName' | 'domain'
>

type InvoiceDetails = {
  billingPeriod: string | undefined
  groupSummary: { description: string; total: string }[]
  totals: Pick<
    Dashboardv99990101Api.Invoices.Invoice,
    'currency' | 'netTotal' | 'grossTotal'
  > & {
    breakdownRows: {
      taxRows: [string, string][]
    }
  }
}

export const InvoicePreviewCard: FC<{
  invoice: Invoice
  customer: Customer
  invoiceDetails: InvoiceDetails
  action?: ReactNode
  startCollapsed?: boolean
}> = ({
  invoice,
  customer,
  invoiceDetails,
  action,
  startCollapsed = false
}) => {
  const [collapsed, setCollapsed] = useState(startCollapsed)
  const [contentRef, contentBounds] = useMeasure()
  const currency = useMemo(() => invoice.currency ?? 'USD', [invoice.currency])

  return (
    <Card
      border={`${GreyGrey40} 1px solid`}
      borderRadius={collapsed ? '8px' : '12px'}
      transition="border-radius 0.2s"
    >
      <motion.div
        initial={false}
        animate={{ height: contentBounds.height || 'auto' }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
      >
        <Box ref={contentRef} position="relative">
          <AnimatePresence initial={false} mode="popLayout">
            {collapsed ? (
              <motion.div key="collapsed" {...crossFadeMotionProps}>
                <CollapsedHeader
                  invoiceNumber={invoice.invoiceNumber}
                  invoiceStatus={invoice.status}
                  customer={customer}
                  currency={currency}
                  grossTotal={invoice.grossTotal}
                  onClick={() => setCollapsed(false)}
                />
              </motion.div>
            ) : (
              <motion.div key="expanded" {...crossFadeMotionProps}>
                <Flex flexDir="column" gap="8px">
                  <Header
                    invoiceNumber={invoice.invoiceNumber}
                    invoiceStatus={invoice.status}
                    customer={customer}
                    currency={currency}
                    grossTotal={invoice.grossTotal}
                    dueDate={invoice.dueDate}
                    onClick={() => setCollapsed(true)}
                  />

                  <InvoiceSummary invoiceDetails={invoiceDetails} />

                  {action && <Flex>{action}</Flex>}
                </Flex>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </motion.div>
    </Card>
  )
}

/**
 * Placeholder shown while the invoice data loads, mirroring the shape of the
 * loaded card (collapsed header, or expanded header + summary block) so the
 * timeline doesn't jump when the data arrives.
 */
export const InvoicePreviewCardSkeleton: FC<{ collapsed?: boolean }> = ({
  collapsed = false
}) => {
  return (
    <Card
      data-testid={INVOICE_PREVIEW_CARD_SKELETON_TESTID}
      border={`${GreyGrey40} 1px solid`}
      borderRadius={collapsed ? '8px' : '12px'}
    >
      {collapsed ? (
        <Grid
          gridTemplateColumns="auto 1fr auto"
          width="100%"
          gridColumnGap="8px"
          alignItems="center"
          padding="3px 4px"
        >
          <Skeleton height="16px" width="16px" borderRadius="50%" />
          <Skeleton height="14px" width="120px" />
          <Skeleton height="14px" width="56px" />
        </Grid>
      ) : (
        <Flex flexDir="column" gap="8px">
          <Flex alignItems="center" gap="12px" paddingX="4px">
            <Skeleton height="32px" width="32px" borderRadius="50%" />
            <Flex justifyContent="space-between" flex="1">
              <Flex flexDir="column" gap="4px">
                <Skeleton height="14px" width="120px" />
                <Skeleton height="14px" width="88px" />
              </Flex>
              <Flex flexDir="column" gap="4px" alignItems="flex-end">
                <Skeleton height="14px" width="72px" />
                <Skeleton height="14px" width="88px" />
              </Flex>
            </Flex>
          </Flex>

          <Flex
            backgroundColor={GreyGrey10}
            borderRadius="8px"
            direction="column"
            gap="8px"
            padding="8px"
          >
            <Skeleton height="14px" width="140px" />
            <Flex
              direction="column"
              gap="8px"
              paddingY="4px"
              paddingBottom="8px"
              borderBottom={`${GreyGrey60} 1px solid`}
            >
              <SummaryLineSkeleton />
              <SummaryLineSkeleton />
            </Flex>
            <Flex justify="space-between" align="center">
              <Skeleton height="14px" width="48px" />
              <Skeleton height="14px" width="80px" />
            </Flex>
          </Flex>
        </Flex>
      )}
    </Card>
  )
}

const SummaryLineSkeleton: FC = () => (
  <Flex justify="space-between">
    <Skeleton height="14px" width="96px" />
    <Skeleton height="14px" width="64px" />
  </Flex>
)

const CollapsedHeader: FC<{
  customer: Customer
  invoiceNumber: string | undefined
  invoiceStatus: Invoice['status']
  grossTotal: string
  currency: Currency
  onClick: () => void
}> = ({
  customer,
  invoiceNumber,
  invoiceStatus,
  grossTotal,
  currency,
  onClick
}) => {
  const title = useMemo(
    () => getHumanReadableInvoiceNumber({ invoiceNumber, invoiceStatus }),
    [invoiceNumber, invoiceStatus]
  )

  return (
    <Grid
      gridTemplateColumns="auto 1fr auto"
      width="100%"
      gridColumnGap="8px"
      alignItems="center"
      padding="3px 4px"
      onClick={onClick}
      cursor="pointer"
    >
      <CustomerLogoCircle customer={customer} size={16} fallbackSize={16} />

      <Text
        {...Primary13SemiBold}
        color={GreyGrey70}
        data-testid={INVOICE_PREVIEW_CARD_TITLE_TESTID}
      >
        {title}
      </Text>

      <Text {...Primary13Regular} color={GreyGrey70}>
        {toMoney({ currency, value: grossTotal })}
      </Text>
    </Grid>
  )
}

const Header: FC<{
  customer: Customer
  invoiceNumber: string | undefined
  invoiceStatus: Invoice['status']
  grossTotal: string
  currency: Currency
  dueDate: string | undefined
  onClick: () => void
}> = ({
  customer,
  invoiceNumber,
  invoiceStatus,
  grossTotal,
  currency,
  dueDate,
  onClick
}) => {
  const formatDueDate = useInvoiceDueDateFormatter()

  const title = useMemo(
    () => getHumanReadableInvoiceNumber({ invoiceNumber, invoiceStatus }),
    [invoiceNumber, invoiceStatus]
  )

  const dueDateText = formatDueDate({
    dueDate,
    formatDate: dueDate => format(new Date(dueDate), 'd MMM yyyy'),
    prefix: 'Due '
  })

  return (
    <Flex
      alignItems="center"
      gap="12px"
      paddingX="4px"
      onClick={onClick}
      cursor="pointer"
    >
      <CustomerLogoCircle customer={customer} size={32} fallbackSize={16} />

      <Flex justifyContent="space-between" flex="1">
        <Flex flexDir="column" gap="4px">
          <Text
            {...Primary13SemiBold}
            color={GreyGrey80}
            data-testid={INVOICE_PREVIEW_CARD_TITLE_TESTID}
          >
            {title}
          </Text>
          <Text {...Primary13Regular} color={GreyGrey70}>
            {customer.legalName}
          </Text>
        </Flex>

        <Flex flexDir="column" gap="4px" textAlign="right">
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            {toMoney({ currency, value: grossTotal })}
          </Text>
          {dueDateText && (
            <Text
              {...Primary13Regular}
              color={GreyGrey70}
              data-testid={INVOICE_PREVIEW_CARD_DUE_DATE_TESTID}
            >
              {dueDateText}
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

const InvoiceSummary: FC<{
  invoiceDetails: InvoiceDetails
}> = ({ invoiceDetails }) => {
  const { billingPeriod, groupSummary, totals } = invoiceDetails
  const currency = totals.currency as Currency

  const hasGroupItems = groupSummary.length > 0
  const hasBillingPeriodOrGroupItems = Boolean(billingPeriod) || hasGroupItems

  return (
    <Flex
      backgroundColor={GreyGrey10}
      borderRadius="8px"
      direction="column"
      gap="8px"
      padding="8px"
    >
      {billingPeriod && (
        <Text {...Primary13SemiBold} color={GreyGrey90}>
          {billingPeriod}
        </Text>
      )}

      <Flex direction="column" gap="8px" paddingY="4px">
        {hasGroupItems && (
          <Flex flexDirection="column" gap="8px">
            {groupSummary.map((item, index) => (
              <SummaryLine
                key={index}
                description={item.description}
                amount={item.total}
              />
            ))}
          </Flex>
        )}

        <Flex flexDirection="column" gap="8px">
          <Flex
            flexDirection="column"
            gap="8px"
            paddingTop={hasBillingPeriodOrGroupItems ? '8px' : '0px'}
            paddingBottom="8px"
            borderTop={
              hasBillingPeriodOrGroupItems ? `${GreyGrey30} 1px solid` : 'none'
            }
            borderBottom={`${GreyGrey60} 1px solid`}
          >
            <SummaryLine
              description="Subtotal"
              amount={toMoney({ currency, value: totals.netTotal })}
              descriptionColor={GreyGrey70}
            />
            {totals.breakdownRows.taxRows.map(([title, amount]) => (
              <SummaryLine
                key={title}
                description={title}
                amount={toMoney({ currency, value: amount })}
                descriptionColor={GreyGrey70}
              />
            ))}
          </Flex>

          <Flex justify="space-between" align="center" color={GreyGrey80}>
            <Text {...Primary13SemiBold}>Total</Text>
            <Text {...Primary13SemiBold}>
              {toMoney({
                value: totals.grossTotal,
                currency,
                appendCurrencyCode: true
              })}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

const SummaryLine: FC<{
  description: string
  amount: string
  descriptionColor?: string
}> = ({ description, amount, descriptionColor = GreyGrey80 }) => (
  <Flex justify="space-between">
    <Text {...Primary13Regular} color={descriptionColor}>
      {description}
    </Text>
    <Text {...Primary13Regular} color={GreyGrey90}>
      {amount}
    </Text>
  </Flex>
)
